<template>
	<CDialog
		:visible="true"
		:is-loading="isLoading"
		:max-width="formWidth"
		custom-class="dialog--wide entry-details entry-details-lightbox-dialog"
		:show-close="true"
		:show-confirm="entryHasChanges"
		:confirm-options="confirmOptions"
		:close-on-click-modal="true"
		:close-on-route-change="false"
		:resources="resources"
		destroy-on-close
		header-is-sticky
		print-fullwidth
		@close="handleClose"
	>
		<template #title>
			<CEntryDetailsLightboxTitle
				:is-person-entry="isPersonEntry"
				:person-submissions-enabled="personSubmissionsEnabled"
				:summary="entrySummary || ''"
				:person-email="personEmail"
				:entry-id="entryId"
				:internal-entry-id="internalEntryId || ''"
				:view-id="viewId || ''"
				:current-view="getCognitoForms()?.model?.currentView || null"
				:form-meta="headerFormMeta"
				:folder-name="folderName"
				:organization-code="organizationCode"
				@navigate="navigateToEntryDetails"
			/>
		</template>
		<div class="entry-details entry-details-lightbox__content entry-details--tabbed">
			<EntryDetailsPrintMetadata
				v-if="entryNumber"
				separated
				:entry-number="entryNumber"
				:summary="entrySummary"
				:status="entryStatus"
				:submitted="dateSubmitted ? format(dateSubmitted) : null"
			/>
			<CEntryDetailsDockedTabbed
				ref="dockedTabbedLayout"
				:person-submissions-enabled="personSubmissionsEnabled"
				:is-person-form-entry="personSubmissionsEnabled && peopleFormEnabled"
				:auto-open-order-dialog="autoOpenOrderDialog"
				:active-tab.sync="activeTab"
				:format-fn="format"
				:can-manage-entries="canManageEntries"
				:can-manage-form="canManageForm"
				:documents="documents"
				:show-docs-for-tabbed="showDocsForTabbed"
				:sharing-enabled="sharingEnabled"
				:incomplete="incomplete"
				:entry-summary="entrySummary || ''"
				:form-name="formTitle || ''"
				:date-submitted="dateSubmitted ? format(dateSubmitted) : null"
				:person-email="personEmail"
				:order="order"
				:customer-card="customerCard"
				:can-edit-entries="canEditEntries"
				:entry-has-changes="entryHasChanges"
				:performing-workflow-action="performingWorkflowAction"
				:manual-payment-form="manualPaymentForm"
				:can-pay-with-square="canPayWithSquare"
				:base-url="baseUrl"
				:square-client-id="squareClientId"
				:currency="currency"
				:currency-code="currencyCode"
				:currency-decimal-digits="currencyDecimalDigits"
				:org-id="orgId"
				:show-entry-activity="showEntryActivity"
				:internal-entry-id="internalEntryId"
				:view-id="viewId"
				:date-created="dateCreated"
				:last-activity-time="lastActivityTime"
				:roles="roles"
				:actions="actions"
				:statuses="statuses"
				:limited-access="isLimitedAccess"
				:entry-status="entryStatus"
				:has-entry="internalEntryId != null"
				:allowed-actions="allowedActions"
				:current-action-id="currentActionId"
				:performing-save-and-resume="performingSaveAndResume"
				:button-status="buttonStatus"
				:save-and-resume-visible="saveAndResumeVisible"
				disable-unread-entry-tracking
				opened-from-lightbox
				@menu-action="handleTabbedMenuAction"
				@perform-action="performAction"
				@discard-changes="discardChanges"
				@reset-button-status="resetButtonStatus"
				@save-entry="saveEntry"
				@select-document="handleSelectDocument"
				@manage-documents="handleManageDocuments"
				@pay-order="handlePayOrder"
				@payment-refunded="handleCognitoPayRefunded"
				@payment-updated="handleCognitoPayUpdated"
				@refund-order="handleRefundOrder"
				@pay-order-with-square-pos="handlePayOrderWithSquarePos"
				@open-feature-dialog="handleOpenFeatureDialog"
				@open-view-email-dialog="handleOpenViewEmailDialog"
				@open-view-integration-dialog="handleOpenViewIntegrationDialog"
				@open-view-google-analytics-dialog="handleOpenViewGoogleAnalyticsDialog"
				@open-view-entry-change-dialog="handleOpenViewEntryChangeDialog"
			>
				<template #details>
					<div id="cognito-form" class="entry-details-lightbox__form app-styled-form">
						<div ref="mountFormHere"></div>
					</div>
				</template>
				<template #submissions="{ boundaryEl }">
					<CEntryDetailsSubmissions v-if="internalEntryId && isPersonEntry" :entry-id="internalEntryId" :boundary-el="boundaryEl" />
				</template>
				<template #orders="{ boundaryEl }">
					<OrdersTable v-if="internalEntryId" :person-entry-id="internalEntryId" :boundary-el="boundaryEl" />
				</template>
			</CEntryDetailsDockedTabbed>
		</div>
		<CognitoPayRefundDialog
			v-if="cognitoPayRefundPayment"
			:visible="showCognitoPayRefundDialog"
			:payment="cognitoPayRefundPayment"
			locality="en-US"
			:max-refund-amount="cognitoPayRefundMaxAmount"
			@close="showCognitoPayRefundDialog = false"
			@payment-refunded="handleCognitoPayRefunded"
		/>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, ref, toRef, onMounted, onBeforeUnmount, nextTick, inject, provide, computed, watch, type PropType } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CEntryDetailsDockedTabbed from 'src/views/entries/EntryDetailsDockedTabbed.vue';
	import CEntryDetailsSubmissions from 'src/views/entries/EntryDetailsSubmissions.vue';
	import OrdersTable from '../admin/OrdersTable.vue';
	import CEntryDetailsLightboxTitle from 'src/views/entries/EntryDetailsLightboxTitle.vue';
	import EntryDetailsPrintMetadata from 'src/views/entries/EntryDetailsPrintMetadata.vue';
	import CognitoPayRefundDialog from 'src/views/admin/cognito-pay/dialogs/CognitoPayRefundDialog.vue';
	import type { CognitoPayRefundResponse } from '@cognitoforms/types/server-types/core/cognito-pay/cognito-pay-refund-response';
	import { useCognitoPayRefund } from 'src/views/entries/entry-details/use-cognito-pay-refund';
	import { PaymentProcessor } from '@cognitoforms/types/server-types/payment/model/payment-processor';
	import type { AdminFormApi } from 'src/views/admin/utils/admin-api';
	import type { EntriesPageFormHandle } from '../../../../client/src/framework/admin/entries-page-form-handle';
	import { EmbedContext } from '../../../../client/src/framework/public/form-handle';
	import type { EntryDetailsTabValue } from 'src/views/entries/entry-details/use-entry-details-tabs';
	import { useEntryDetailsLightbox } from 'src/views/entries/entry-details/use-entry-details-lightbox';
	import type { CognitoFormsController } from './entry-details/entry-details-lightbox.types';
	import { localizationResourcesKey, entryDetailsLightboxIdKey } from 'src/utilities/injection-keys';
	import { getEntryAvailabilityState } from './entry-details/use-entry-details-lightbox-availability';
	import { useEntryDetailsCore } from './entry-details/use-entry-details-core';
	import { resolveEntryViewContainingEntry } from '@cognitoforms/api/services/submitter-person-service';
	import { completeCognitoPayInteraction, type CognitoPayTelemetryHandle } from 'src/views/admin/cognito-pay/cognito-pay-telemetry';

	export default defineComponent({
		name: 'CEntryDetailsLightbox',
		components: {
			CDialog,
			CEntryDetailsDockedTabbed,
			CEntryDetailsSubmissions,
			OrdersTable,
			CEntryDetailsLightboxTitle,
			CognitoPayRefundDialog,
			EntryDetailsPrintMetadata
		},
		props: {
			adminApi: {
				type: Object as PropType<AdminFormApi>,
				required: true
			},
			lightboxId: {
				type: String,
				default: ''
			},
			entryId: {
				type: String,
				required: true
			},
			formId: {
				type: String,
				required: true
			},
			viewId: {
				type: String,
				default: null
			},
			viewToken: {
				type: String,
				default: null
			},
			roleId: {
				type: Number,
				default: null
			},
			title: {
				type: String,
				default: 'Entry Details'
			},
			defaultTab: {
				type: String as PropType<EntryDetailsTabValue>,
				default: 'details'
			},
			canEdit: {
				type: Boolean,
				default: false
			},
			personEmail: {
				type: String,
				default: ''
			},
			telemetry: {
				type: Object as PropType<CognitoPayTelemetryHandle | null>,
				default: null
			}
		},
		emits: ['close'],
		setup(props, { emit }) {
			let entryAdapter: EntriesPageFormHandle | null = null;
			let originalEntryUpdatedHandler: CognitoFormsController['entryUpdated'] | null = null;
			let originalDeleteEntriesHandler: CognitoFormsController['deleteEntries'] | undefined;
			const { invokeRefreshCallback } = useEntryDetailsLightbox();

			provide(entryDetailsLightboxIdKey, props.lightboxId);

			const resources = inject(localizationResourcesKey);
			const activeTab = ref<EntryDetailsTabValue>(props.defaultTab);
			const formWidth = ref('');
			const mountFormHere = ref<HTMLElement | null>(null);
			const dockedTabbedLayout = ref<InstanceType<typeof CEntryDetailsDockedTabbed> | null>(null);
			let isMounted = true;

			const core = useEntryDetailsCore({
				formId: toRef(props, 'formId'),
				entryId: toRef(props, 'entryId'),
				viewId: toRef(props, 'viewId'),
				viewToken: toRef(props, 'viewToken'),
				roleId: toRef(props, 'roleId'),
				title: toRef(props, 'title'),
				getEntryAdapter: () => entryAdapter,
				getAdminApi: () => props.adminApi,
				onClose: () => emit('close'),
				prepareForPrint: () => dockedTabbedLayout.value?.prepareForPrint?.(),
				canEditEntry: () => props.canEdit
			});

			const isPersonEntry = computed(() => core.personSubmissionsEnabled.value && core.peopleFormEnabled.value);
			const headerFormMeta = computed(() => core.getFormMetaFromStore?.() || null);
			const canEditEntries = computed(() => core.canEditEntries.value || props.canEdit);

			const autoOpenOrderDialog = computed(() => props.defaultTab === 'order' && core.personSubmissionsEnabled.value && core.peopleFormEnabled.value);
			watch(autoOpenOrderDialog, (val) => {
				if (val && activeTab.value === 'order')
					activeTab.value = 'orders';
			}, { immediate: true });

			/**
			 * Entry-lightbox timing completes only after the current lightbox has finished loading
			 * the selected entry and is no longer showing its loading state.
			 */
			watch(() => [props.telemetry?.interactionKey, core.isLoading.value, core.internalEntryId.value], () => {
				if (!props.telemetry || core.isLoading.value || !core.internalEntryId.value)
					return;

				completeCognitoPayInteraction(props.telemetry, {
					formId: props.formId,
					properties: {
						EntryId: props.entryId
					}
				});
			}, { immediate: true });

			const navigateToEntryDetails = (targetRoute: string) => {
				window.open(targetRoute, '_blank', 'noopener');
			};

			const resolveInitialView = () => {
				let resolvedViewId = props.viewId ?? null;
				let resolvedViewToken = props.viewToken ?? null;

				const currentView = core.getCognitoForms()?.model?.currentView;
				if (!resolvedViewId)
					resolvedViewId = currentView?.get_Id?.() ?? null;

				if (!resolvedViewToken && resolvedViewId && currentView?.get_Id?.() === resolvedViewId)
					resolvedViewToken = currentView.get_Token?.() ?? null;

				const sessionViewTokens = entryAdapter?.form?.session?.viewTokens as Record<string, string> | undefined;
				if (sessionViewTokens) {
					if (resolvedViewId && !resolvedViewToken)
						resolvedViewToken = sessionViewTokens[resolvedViewId] ?? null;

					if (!resolvedViewId || !resolvedViewToken) {
						const firstViewWithToken = Object.entries(sessionViewTokens).find(([, token]) => !!token);
						if (firstViewWithToken) {
							resolvedViewId = resolvedViewId ?? firstViewWithToken[0];
							resolvedViewToken = resolvedViewToken ?? firstViewWithToken[1];
						}
					}
				}

				return {
					viewId: resolvedViewId,
					viewToken: resolvedViewToken
				};
			};

			const shouldTrackEntryChange = (event: unknown) => {
				const onEntryChanged = (window as unknown as {
					Cognito?: {
						Forms?: {
							onEntryChanged?: (changeEvent: unknown) => boolean;
						};
					};
				}).Cognito?.Forms?.onEntryChanged;

				if (typeof onEntryChanged === 'function')
					return onEntryChanged(event);

				return true;
			};

			const mountForm = async () => {
				if (!mountFormHere.value) {
					core.isLoading.value = false;
					return;
				}

				try {
					const formDef = await props.adminApi.getFormDefinition(props.formId, EmbedContext.Entries);
					const widthMatch = formDef.theme?.style?.textContent?.match(/--form__width:\s*([^;]+);/);
					if (widthMatch)
						formWidth.value = widthMatch[1].trim();

					entryAdapter = await props.adminApi.mount(props.formId, mountFormHere.value as HTMLElement, EmbedContext.Entries) as EntriesPageFormHandle;

					const initialView = resolveInitialView();
					if (!initialView.viewId || !initialView.viewToken) {
						core.isLoading.value = false;
						if (!isMounted)
							return;
						core.closeForUnavailableEntry('unavailable');
						return;
					}

					await entryAdapter.changeView(initialView.viewId, initialView.viewToken, false);

					try {
						await entryAdapter.entrySet?.refresh?.();
					}
					catch {
						core.isLoading.value = false;
						if (!isMounted)
							return;
						core.closeForUnavailableEntry('unavailable');
						return;
					}

					let resolvedRoleId: number | null = null;
					const tryResolveAndRetryView = async (): Promise<number> => {
						const resolved = await resolveEntryViewContainingEntry(props.formId, props.entryId);
						if (!resolved || !isMounted)
							return -1;

						resolvedRoleId = resolved.RoleId;

						await entryAdapter?.changeView(resolved.ViewId, resolved.Token, false);

						try {
							await entryAdapter?.entrySet?.refresh?.();
						}
						catch {
							return -1;
						}

						const retriedIndex = entryAdapter?.entrySet?.indexOfEntryId(props.entryId);
						return typeof retriedIndex === 'number' ? retriedIndex : -1;
					};

					let entryIndex = entryAdapter.entrySet?.indexOfEntryId(props.entryId);
					if (typeof entryIndex !== 'number' || entryIndex < 0) {
						entryIndex = await tryResolveAndRetryView();
						if (!isMounted)
							return;

						if (entryIndex < 0) {
							core.isLoading.value = false;
							core.closeForUnavailableEntry('unavailable');
							return;
						}
					}

					const entryKey = entryAdapter.entrySet?.entryKeyAt(entryIndex) ?? core.entryKeyFromId.value;
					if (!entryKey?.EntryId) {
						core.isLoading.value = false;
						if (!isMounted)
							return;
						core.closeForUnavailableEntry('unavailable');
						return;
					}

					const roleId = resolvedRoleId != null ? resolvedRoleId : props.roleId;
					await entryAdapter.setExistingEntry(entryKey, core.getRoleNameFromId(roleId), !props.canEdit);

					const availabilityState = getEntryAvailabilityState(entryAdapter?.entry, props.entryId);
					if (availabilityState) {
						core.isLoading.value = false;
						if (!isMounted)
							return;
						core.closeForUnavailableEntry(availabilityState);
						return;
					}

					core.hydrateFormMetaFromModel(entryAdapter);
					await core.loadCurrentForm(entryAdapter);
					await core.syncEntryData();
					if (!core.internalEntryId.value) {
						core.isLoading.value = false;
						if (!isMounted)
							return;
						core.closeForUnavailableEntry('unavailable');
						return;
					}

					// Register event handlers after initial sync to avoid spurious calls during form initialization
					entryAdapter.addChangesDetected((event) => {
						if (core.performingWorkflowAction.value || core.performingSaveAndResume.value)
							return;

						if (!shouldTrackEntryChange(event))
							return;

						core.entryHasChanges.value = true;
					});

					await entryAdapter.addFormEventsHandler(core.actionPerformed, 'afterSubmit');
					await entryAdapter.addFormEventsHandler(core.entryReset, 'resetEntry');
					await entryAdapter.addFormEventsHandler(core.handleOrderUpdated, 'orderUpdated');
					await entryAdapter.addFormEventsHandler(core.onAllowedActionsUpdate, 'allowedActionsChanged');
					await entryAdapter.addFormEventsHandler(core.onActionUpdate, 'actionChanged');
					await entryAdapter.addFormEventsHandler(core.handleDownloadOverLimit, 'tryDownloadOverLimit');
					await entryAdapter.addFormEventsHandler(core.updateLastActivityTime, 'activityPerformed');

					const handleEntryPersisted = () => {
						core.entryHasChanges.value = false;
						core.buttonStatus.value = 'success';
						core.updateLastActivityTime();
						core.refreshAllowedActions().then(core.syncCurrentAction);
					};

					entryAdapter.on('afterSubmit', handleEntryPersisted);
					entryAdapter.on('afterSave', handleEntryPersisted);
				}
				catch (error) {
					console.error('Failed to open entry details lightbox.', error);
				}
				finally {
					core.isLoading.value = false;
				}
			};

			const installEntryUpdatedListener = () => {
				const controller = core.getCognitoForms()?.controller;
				if (!controller?.entryUpdated || originalEntryUpdatedHandler)
					return;

				const originalEntryUpdated = controller.entryUpdated;
				originalEntryUpdatedHandler = originalEntryUpdated;

				controller.entryUpdated = ((entryKey, viewEntry, callback) => {
					originalEntryUpdated(entryKey, viewEntry, callback);

					if (!entryKey?.EntryId || entryKey.EntryId !== core.internalEntryId.value)
						return;

					core.refreshEntryFromServer(entryKey);
					invokeRefreshCallback(props.entryId);
				}) as CognitoFormsController['entryUpdated'];
			};

			const restoreEntryUpdatedListener = () => {
				const controller = core.getCognitoForms()?.controller;
				if (!controller || !originalEntryUpdatedHandler)
					return;

				controller.entryUpdated = originalEntryUpdatedHandler;
				originalEntryUpdatedHandler = null;
			};

			const disableControllerDelete = () => {
				const controller = core.getCognitoForms()?.controller;
				if (!controller?.deleteEntries || originalDeleteEntriesHandler)
					return;

				originalDeleteEntriesHandler = controller.deleteEntries;
				controller.deleteEntries = undefined;
			};

			const restoreControllerDelete = () => {
				const controller = core.getCognitoForms()?.controller;
				if (!controller || !originalDeleteEntriesHandler)
					return;

				controller.deleteEntries = originalDeleteEntriesHandler;
				originalDeleteEntriesHandler = undefined;
			};

			const handleClose = () => {
				emit('close');
			};

			const {
				showCognitoPayRefundDialog,
				cognitoPayRefundPayment,
				cognitoPayRefundMaxAmount,
				openCognitoPayRefundDialog,
				closeCognitoPayRefundDialog
			} = useCognitoPayRefund();

			const handleRefundOrder = async ({ isCash, paymentId = null }: { isCash: boolean, paymentId: string | null }) => {
				if (!isCash) {
					const orderValue = core.order.value as Record<string, unknown> | null;
					const processorName = typeof orderValue?.ProcessorName === 'string' ? orderValue.ProcessorName : '';
					if (processorName === PaymentProcessor.CognitoPay && paymentId) {
						const opened = await openCognitoPayRefundDialog(paymentId);
						if (opened) return;
					}
				}

				return core.handleRefundOrder(isCash);
			};

			const handleCognitoPayRefunded = async (_response: CognitoPayRefundResponse) => {
				closeCognitoPayRefundDialog();
				invokeRefreshCallback(props.entryId);
				// Use a non-matching ETag so the entry-set cache check fails and forces
				// a server re-fetch, picking up the updated order with the new refund transaction.
				// A timestamp suffix ensures this never matches a previously-cached 'stale' entry
				// on a subsequent refund (the service caches with the request ETag as the key).
				const entryKey = { ...core.getCurrentEntryKey(), EntryETag: `stale-${Date.now()}` };
				await core.refreshEntryFromServer(entryKey);
				core.notifyEntryDataChanged();
			};

			const handleCognitoPayUpdated = async () => {
				invokeRefreshCallback(props.entryId);
				const entryKey = { ...core.getCurrentEntryKey(), EntryETag: `stale-${Date.now()}` };
				await core.refreshEntryFromServer(entryKey);
				core.notifyEntryDataChanged();
			};

			const confirmOptions = {
				showClose: true,
				title: resources.value.UnsavedChanges,
				message: resources.value.AreYouSureYouWantToDiscardYourChanges,
				confirmButtonText: resources.value.DiscardChanges,
				cancelButtonText: resources.value.KeepEditing,
				customClass: 'notification notification--confirmation-dialog',
				confirmButtonClass: 'button button--secondary sz-3',
				cancelButtonClass: 'button button--primary sz-3'
			};

			onMounted(() => {
				nextTick(() => {
					document.body.classList.add('app-styled-form');
					installEntryUpdatedListener();
					disableControllerDelete();
					mountForm();
				});
			});

			onBeforeUnmount(() => {
				isMounted = false;
				if (entryAdapter)
					props.adminApi.destroyHandle(entryAdapter);
				restoreEntryUpdatedListener();
				restoreControllerDelete();
				document.body.classList.remove('app-styled-form');
				entryAdapter = null;
			});

			return {
				...core,
				canEditEntries,
				resources,
				activeTab,
				formWidth,
				confirmOptions,
				mountFormHere,
				dockedTabbedLayout,
				handleClose,
				handleRefundOrder,
				handleCognitoPayRefunded,
				handleCognitoPayUpdated,
				showCognitoPayRefundDialog,
				cognitoPayRefundPayment,
				cognitoPayRefundMaxAmount,
				isPersonEntry,
				headerFormMeta,
				navigateToEntryDetails,
				autoOpenOrderDialog
			};
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/app/cognito-form.scss"></style>

<style lang="scss">
@import '@legacy/Views/Admin/entry-details';

.entry-details-lightbox {

	&__title {
		@include nowrap;
		font-weight: 600;
	}

	&__date {
		color: var(--text-subtle);
		font-size: $size__1;
	}

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;

		:root & .entry-details__tabs-container {
			padding: 0 var(--spacing-modal-padding) 0 calc(var(--spacing-modal-padding) - 9px);
		}

		:root & .tab-content {
			padding: var(--spacing-5) var(--spacing-modal-padding);

			// Leave room for scrollbar gutter
			padding-right: calc(var(--spacing-5) - 8px);
		}
	}

	&__form {
		height: 100%;
		min-height: 100%;

		.cog-page__navigation,
		.cog-thank-you {
			display: none !important;
		}

		.cog-header,
		.cog-payment,
		.el-cog-payment,
		.cog-section.cog-payment {
			display: none !important;
		}
	}

	// Tabs overrides for lightbox context
	&__content .entry-details__tabs {
		flex: 0 0 auto;
		width: auto;
		min-width: 0;
	}
}

.entry-details-lightbox-dialog {
	// Bounds the dialog itself to the space .el-dialog__wrapper actually reserves for it
	// (viewport height minus its own top/bottom padding), so the flex chain below has real
	// free space to distribute instead of everything just sizing to content.
	display: flex;
	flex-direction: column;
	min-height: 500px;
	max-height: calc(100vh - #{$space__4} * 2);

	.el-dialog__header {
		flex: 0 0 auto;
		padding-right: $space__touch-target + $space__4;
	}

	.el-dialog__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		overflow: visible;
		padding: 0;
	}

	.dialog__content-container {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
	}

	.dialog__button-bar {
		flex: 0 0 auto;
	}

	.c-forms-payment-summary {
		width: 54% !important;
	}

	.c-forms-payment-order-summary {
		width: 46% !important;
		padding-right: 0 !important;
	}

	.entry-details-button-bar.c-admin-fixed-action-bar {
		position: sticky;
		bottom: 0;
		width: 100%;
		background: var(--shade-2);
		padding-bottom: calc(#{$space__3} + env(safe-area-inset-bottom, 0px));
	}

	// Mirrors the docked-panel override in EntryDetails.vue (#c-entry-details)
	// size the table to the dialog's free space so a scrollbar only shows once rows actually overflow
	.entry-details-tabbed-layout {
		flex: 1;
	}

	.entry-details__tab-content {
		display: flex;
		flex-direction: column;
	}

	.tab-content--submissions,
	.tab-content--orders {
		display: flex;
		flex: 1;
		flex-direction: column;
		height: 100%;
		min-height: 0;

		.submissions-table,
		.orders-table__wrapper,
		.orders-table,
		.table-component,
		.table-component__scroll-container {
			display: flex;
			flex: 0 1 auto;
			flex-direction: column;
			min-height: 0;
		}

		.table-component__body {
			// .entry-details--tabbed .table-component__body (EntryDetails.vue) sets a
			// competing max-height at equal selector specificity; !important guarantees
			// this wins regardless of the two components' stylesheet load order.
			flex: 0 1 auto !important;
			max-height: none !important;
		}

		.orders-table__wrapper {
			margin-bottom: $space__5;
		}

		.tab-content--orders__cards-on-file {
			margin-right: calc(var(--spacing-5) * -1);
			margin-bottom: calc(var(--spacing-5) * -1);
			margin-left: calc(var(--spacing-5) * -1);
			padding-right: var(--spacing-5);
			padding-left: var(--spacing-5);
		}
	}
}

@media print {

	.entry-details-lightbox__content {

		.entry-details__tabs-container {
			padding-top: $space__4 !important;
			padding-left: 0 !important;
		}

		.entry-details__tab-content {
			overflow: visible !important;
		}

		.table-component__body {
			max-height: none !important;
			overflow: visible !important;
		}

		.tab-content {
			padding: $space__4 !important;
		}
	}
}
</style>
